import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '../utils/pensionCalc';
import api from '../utils/api';

// Fields that should not be editable (system/derived fields)
const NON_EDITABLE_FIELDS = new Set([
  'employee_name', 'p45_auto_issued', 'previous_salary', 'previous_monthly_salary',
  'new_annual_salary', 'new_monthly_salary',
]);

// Display order for new starter fields (matching the form layout)
const NEW_STARTER_FIELD_ORDER = [
  // Personal Details
  'first_name', 'last_name', 'email', 'gender', 'date_of_birth', 'ni_number',
  // Address
  'address_line_1', 'address_line_2', 'city', 'postcode',
  // Employment Details
  'start_date', 'job_title', 'contract_type', 'contract_end_date',
  'employment_type', 'part_time_basis', 'is_director', 'director_ni_method', 'director_appointed_date',
  // Compensation
  'annual_salary', 'prorata_salary', 'pay_frequency',
  // Bank Details
  'bank_account_name', 'sort_code', 'account_number',
  // Pension
  'pension_enroll', 'deferral_months', 'pension_earnings_basis',
  'pension_employer_type', 'pension_employer_value',
  'pension_employee_type', 'pension_employee_value',
];

// Internal flags the UI branches on but which nobody needs to read as a row.
const INTERNAL_FIELDS = new Set(['is_part_time', 'pt_fraction']);

// Whether a field is worth showing at all. Two reasons one might not be: it is an
// internal flag, or it only applies given another field's value — in which case a stale
// default left behind by the form would read as a contradictory instruction.
// deferral_months is the latter: seeded to its default (3) on the starter form and not
// cleared when the client switches to immediate enrolment, so older records still carry it.
function isFieldApplicable(key, data) {
  if (INTERNAL_FIELDS.has(key)) return false;
  if (key === 'deferral_months') {
    return (data.pension_enroll || data.pension_enrollment) === 'defer';
  }
  return true;
}

// Returns entries sorted by form order for new_starter, or original order for others
function getOrderedEntries(data, changeType) {
  if (changeType !== 'new_starter') {
    return Object.entries(data).filter(([key]) => isFieldApplicable(key, data));
  }
  const entries = Object.entries(data).filter(([key]) => isFieldApplicable(key, data));
  const orderMap = {};
  NEW_STARTER_FIELD_ORDER.forEach((key, i) => { orderMap[key] = i; });
  return entries.sort((a, b) => {
    const aIdx = orderMap[a[0]] !== undefined ? orderMap[a[0]] : 999;
    const bIdx = orderMap[b[0]] !== undefined ? orderMap[b[0]] : 999;
    return aIdx - bIdx;
  });
}

// Fields that should render as a select
const FIELD_OPTIONS = {
  director_ni_method: [
    { value: 'alternative', label: 'Alternative method' },
    { value: 'annual', label: 'Annual method' },
    { value: 'unsure', label: 'Not sure — accountant to decide' },
  ],
  reason: [
    { value: 'resignation', label: 'Resignation' },
    { value: 'redundancy', label: 'Redundancy' },
    { value: 'retirement', label: 'Retirement' },
    { value: 'dismissal', label: 'Dismissal' },
    { value: 'end-of-contract', label: 'End of Contract' },
    { value: 'other', label: 'Other' },
  ],
  leave_type: [
    { value: 'maternity', label: 'Maternity' },
    { value: 'paternity', label: 'Paternity' },
    { value: 'shared_parental', label: 'Shared Parental' },
    { value: 'adoption', label: 'Adoption' },
  ],
  pay_type: [
    { value: 'statutory', label: 'Statutory' },
    { value: 'enhanced', label: 'Enhanced' },
    { value: 'unpaid', label: 'Unpaid' },
  ],
};

function ChangeCard({ change, onDelete, onUpdate }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const data = typeof change.change_data === 'string' ? JSON.parse(change.change_data) : change.change_data;

  const typeLabels = {
    new_starter: 'New Starter',
    leaver: 'Leaver',
    pension: 'Pension Update',
    salary: 'Salary Change',
    parental_leave: 'Parental Leave',
    sick_leave: 'Sick Leave',
    unpaid_leave: 'Unpaid Leave',
    addition: 'Addition',
    deduction: 'Deduction',
    bank_details: 'Bank Details',
    address_change: 'Address Change',
    personal_details: 'Personal Details',
    contractual_change: 'Contractual Change',
    commission: 'Commission',
  };

  const typeColors = {
    new_starter: 'bg-green-100 text-green-800 border-green-200',
    leaver: 'bg-red-100 text-red-800 border-red-200',
    pension: 'bg-blue-100 text-blue-800 border-blue-200',
    salary: 'bg-purple-100 text-purple-800 border-purple-200',
    parental_leave: 'bg-pink-100 text-pink-800 border-pink-200',
    sick_leave: 'bg-orange-100 text-orange-800 border-orange-200',
    addition: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    deduction: 'bg-amber-100 text-amber-800 border-amber-200',
    bank_details: 'bg-cyan-100 text-cyan-800 border-cyan-200',
    address_change: 'bg-teal-100 text-teal-800 border-teal-200',
    personal_details: 'bg-teal-100 text-teal-800 border-teal-200',
    contractual_change: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    commission: 'bg-brand-100 text-brand-800 border-brand-200',
  };

  const employeeName = change.first_name && change.last_name
    ? `${change.first_name} ${change.last_name}`
    : data.first_name
      ? `${data.first_name} ${data.last_name}`
      : data.employee_name || 'Unknown';

  const startEdit = () => {
    setEditData({ ...data });
    setEditing(true);
    setExpanded(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setEditData({});
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.patch(`/pending-changes/${change.id}`, { change_data: editData });
      if (onUpdate) onUpdate();
      setEditing(false);
      setEditData({});
    } catch (err) {
      console.error('Failed to update change:', err);
    } finally {
      setSaving(false);
    }
  };

  // These are applied to the employee record on save, so editing the change alone
  // would leave the two out of step — undo and re-enter instead. Pay-type
  // conversions and hourly-rate changes matter most: hourly pay is worked out from
  // the record. Mirrors isRateChangeOnRecord in routes/pendingChanges.js.
  const isRateChangeOnRecord =
    (change.change_type === 'contractual_change'
      && (data.change_type === 'salaried_to_hourly' || data.change_type === 'hourly_to_salaried'))
    || (change.change_type === 'salary' && !!data.new_hourly_rate);
  const isEditable = change.change_type !== 'commission'
    && change.change_type !== 'personal_details'
    && !isRateChangeOnRecord;

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 border-l-4 border-l-yellow-400 rounded-lg p-3 sm:p-4 mb-3">
      <div className="flex items-start sm:items-center justify-between gap-2">
        <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-gray-500 hover:text-gray-700 p-1 transform transition-transform flex-shrink-0"
            style={{ transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
          >
            &#9654;
          </button>
          <span className={`px-2 py-1 rounded text-xs font-medium border flex-shrink-0 ${typeColors[change.change_type] || 'bg-gray-100 text-gray-800 border-gray-200'}`}>
            {typeLabels[change.change_type] || change.change_type}
          </span>
          <span className="font-medium text-gray-800 truncate">{employeeName}</span>
          {change.change_type === 'commission' && data.commission && (
            <span className="text-sm text-gray-600 font-medium flex-shrink-0">{formatCurrency(data.commission)}</span>
          )}
        </div>
        <div className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
          <span className="text-xs text-gray-500 hidden sm:inline">
            {change.created_at ? new Date(change.created_at).toLocaleString('en-GB') : ''}
          </span>
          {isEditable && !editing && onUpdate && (
            <button
              onClick={() => {
                if (change.change_type === 'new_starter') {
                  navigate(`/new-starter?edit=${change.id}`);
                } else {
                  startEdit();
                }
              }}
              className="text-sm text-brand-500 hover:text-brand-700 font-medium px-2 py-1"
              title="Edit this change"
            >
              Edit
            </button>
          )}
          {onDelete && (
            <button
              onClick={() => onDelete(change.id)}
              className="text-sm text-red-500 hover:text-red-700 font-medium px-2 py-1"
              title="Undo this change and revert to previous state"
            >
              Undo
            </button>
          )}
        </div>
      </div>

      {expanded && !editing && (
        <div className="mt-3 ml-4 sm:ml-8 p-3 bg-gray-50 dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600 text-sm">
          {getOrderedEntries(data, change.change_type).map(([key, value]) => (
            <div key={key} className="flex flex-col sm:flex-row py-1">
              <span className="sm:w-40 text-gray-500 capitalize text-xs sm:text-sm">{key.replace(/_/g, ' ')}:</span>
              <span className="text-gray-800 dark:text-gray-200 break-all">{
                typeof value === 'boolean' ? (value ? 'Yes' : 'No')
                : typeof value === 'object' && value !== null
                  ? (Array.isArray(value) ? value.join(', ') : Object.entries(value).filter(([,v]) => v).map(([k]) => k.replace(/_/g, ' ')).join(', ') || 'None')
                  : key.includes('date') && /^\d{4}-\d{2}-\d{2}/.test(String(value))
                    ? new Date(value).toLocaleDateString('en-GB')
                    : (FIELD_OPTIONS[key]
                      ? (FIELD_OPTIONS[key].find(o => o.value === value)?.label || String(value).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()))
                      : String(value))
              }</span>
            </div>
          ))}
        </div>
      )}

      {expanded && editing && (
        <div className="mt-3 ml-4 sm:ml-8 p-4 bg-blue-50 dark:bg-gray-700 rounded border border-blue-200 dark:border-gray-600 text-sm space-y-3">
          {Object.entries(editData).map(([key, value]) => {
            if (!isFieldApplicable(key, editData)) return null;
            if (NON_EDITABLE_FIELDS.has(key)) {
              return (
                <div key={key} className="flex flex-col sm:flex-row sm:items-center py-1">
                  <span className="sm:w-40 text-gray-500 capitalize text-xs sm:text-sm">{key.replace(/_/g, ' ')}:</span>
                  <span className="text-gray-600">{typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)}</span>
                </div>
              );
            }

            const options = FIELD_OPTIONS[key];
            if (options) {
              return (
                <div key={key} className="flex flex-col sm:flex-row sm:items-center py-1">
                  <label className="sm:w-40 text-gray-600 capitalize text-xs sm:text-sm font-medium">{key.replace(/_/g, ' ')}:</label>
                  <select
                    value={value || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, [key]: e.target.value }))}
                    className="flex-1 text-sm rounded border-gray-300 focus:border-brand-500 focus:ring-brand-500"
                  >
                    {options.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              );
            }

            if (typeof value === 'boolean') {
              return (
                <div key={key} className="flex items-center py-1">
                  <label className="sm:w-40 text-gray-600 capitalize text-xs sm:text-sm font-medium">{key.replace(/_/g, ' ')}:</label>
                  <input
                    type="checkbox"
                    checked={value}
                    onChange={(e) => setEditData(prev => ({ ...prev, [key]: e.target.checked }))}
                    className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                  />
                </div>
              );
            }

            // Date fields
            if (key.includes('date')) {
              return (
                <div key={key} className="flex flex-col sm:flex-row sm:items-center py-1">
                  <label className="sm:w-40 text-gray-600 capitalize text-xs sm:text-sm font-medium">{key.replace(/_/g, ' ')}:</label>
                  <input
                    type="date"
                    value={value || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, [key]: e.target.value }))}
                    className="flex-1 text-sm rounded border-gray-300 focus:border-brand-500 focus:ring-brand-500"
                  />
                </div>
              );
            }

            // Numeric fields
            if (key.includes('amount') || key.includes('salary') || key.includes('pay') || key.includes('days')) {
              return (
                <div key={key} className="flex flex-col sm:flex-row sm:items-center py-1">
                  <label className="sm:w-40 text-gray-600 capitalize text-xs sm:text-sm font-medium">{key.replace(/_/g, ' ')}:</label>
                  <input
                    type="number"
                    step="0.01"
                    value={value || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, [key]: e.target.value }))}
                    className="flex-1 text-sm rounded border-gray-300 focus:border-brand-500 focus:ring-brand-500"
                  />
                </div>
              );
            }

            // Long text fields
            if (key === 'notes' || key === 'final_payment_adjustments') {
              return (
                <div key={key} className="flex flex-col sm:flex-row sm:items-start py-1">
                  <label className="sm:w-40 text-gray-600 capitalize text-xs sm:text-sm font-medium pt-1">{key.replace(/_/g, ' ')}:</label>
                  <textarea
                    rows="3"
                    value={value || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, [key]: e.target.value }))}
                    className="flex-1 text-sm rounded border-gray-300 focus:border-brand-500 focus:ring-brand-500"
                  />
                </div>
              );
            }

            // Default: text input
            return (
              <div key={key} className="flex flex-col sm:flex-row sm:items-center py-1">
                <label className="sm:w-40 text-gray-600 capitalize text-xs sm:text-sm font-medium">{key.replace(/_/g, ' ')}:</label>
                <input
                  type="text"
                  value={value || ''}
                  onChange={(e) => setEditData(prev => ({ ...prev, [key]: e.target.value }))}
                  className="flex-1 text-sm rounded border-gray-300 focus:border-brand-500 focus:ring-brand-500"
                />
              </div>
            );
          })}

          <div className="flex justify-end space-x-2 pt-2 border-t border-blue-200">
            <button
              onClick={cancelEdit}
              className="px-3 py-1.5 text-sm text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={saveEdit}
              disabled={saving}
              className="px-3 py-1.5 text-sm text-white bg-brand-600 rounded hover:bg-brand-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function PendingChanges({ changes, onDelete, commissionEntries, onUpdate }) {
  const allItems = [
    ...(changes || []),
    ...(commissionEntries || []).map(emp => ({
      id: `commission-${emp.id}`,
      change_type: 'commission',
      first_name: emp.first_name,
      last_name: emp.last_name,
      change_data: {
        employee_name: `${emp.first_name} ${emp.last_name}`,
        commission: emp.commission,
      },
    })),
  ];

  if (allItems.length === 0) return null;

  return (
    <div className="mb-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 gap-1">
        <h3 className="text-lg font-semibold text-gray-800">
          Changes This Period ({allItems.length})
        </h3>
        <p className="text-xs text-gray-400">These changes will be reported to your payroll provider on submission</p>
      </div>
      {allItems.map(change => (
        <ChangeCard
          key={change.id}
          change={change}
          onDelete={change.change_type !== 'commission' ? onDelete : null}
          onUpdate={onUpdate}
        />
      ))}
    </div>
  );
}
